import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

class ReservationService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/reservations`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminData');
          window.location.href = '/admin';
        }
        return Promise.reject(error);
      }
    );
  }

  // Obtener reservas agrupadas
  async getGroupedReservations(params = {}) {
    try {
      const response = await this.api.get('/grouped/all', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener reservas');
      throw new Error(errorMessage);
    }
  }

  // Obtener todas las reservas
  async getAllReservations(params = {}) {
    try {
      const response = await this.api.get('/', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener reservas');
      throw new Error(errorMessage);
    }
  }

  // Obtener reservas por evento
  async getReservationsByEvent(eventId) {
    try {
      const response = await this.api.get(`/event/${eventId}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener reservas del evento');
      throw new Error(errorMessage);
    }
  }

  // Obtener reservas por Alumno
  async getReservationsByStudent(studentId, eventId = null) {
    try {
      const params = eventId ? { eventId } : {};
      const response = await this.api.get(`/student/${studentId}`, { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener reservas del Alumno');
      throw new Error(errorMessage);
    }
  }

  // Obtener estadísticas de reservas de un evento
  async getEventReservationStats(eventId) {
    try {
      const response = await this.api.get(`/event/${eventId}/stats`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener estadísticas del evento');
      throw new Error(errorMessage);
    }
  }

  // Obtener asientos ocupados de un evento
  async getOccupiedSeats(eventId) {
    try {
      const response = await this.api.get(`/event/${eventId}/occupied-seats`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener asientos ocupados');
      throw new Error(errorMessage);
    }
  }

  // Verificar disponibilidad de un asiento
  async checkSeatAvailability(eventId, compositeId) {
    try {
      const response = await this.api.get('/check-availability', {
        params: { eventId, compositeId }
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al verificar disponibilidad');
      throw new Error(errorMessage);
    }
  }

  // Obtener una reserva por ID
  async getReservationById(id) {
    try {
      const response = await this.api.get(`/${id}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener reserva');
      throw new Error(errorMessage);
    }
  }

  // Actualizar una reserva
  async updateReservation(id, updateData) {
    try {
      const response = await this.api.patch(`/${id}`, updateData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al actualizar reserva');
      throw new Error(errorMessage);
    }
  }

  // Confirmar una reserva
  async confirmReservation(id) {
    try {
      const response = await this.api.patch(`/${id}/confirm`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al confirmar reserva');
      throw new Error(errorMessage);
    }
  }

  // Cancelar una reserva
  async cancelReservation(id) {
    try {
      const response = await this.api.patch(`/${id}/cancel`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cancelar reserva');
      throw new Error(errorMessage);
    }
  }

  // Completar una reserva
  async completeReservation(id) {
    try {
      const response = await this.api.patch(`/${id}/complete`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al completar reserva');
      throw new Error(errorMessage);
    }
  }

  // Marcar reserva como vendida
  async markAsSold(id) {
    try {
      const response = await this.api.patch(`/${id}/mark-sold`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al marcar como vendida');
      throw new Error(errorMessage);
    }
  }

  // Marcar entrada como sin costo (solo administradores)
  async markAsNoCost(id, reason) {
    try {
      const response = await this.api.patch(`/${id}/mark-no-cost`, { reason });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al marcar sin costo');
      throw new Error(errorMessage);
    }
  }

  // Marcar reserva como entregada
  async markAsDelivered(id) {
    try {
      const response = await this.api.patch(`/${id}/mark-delivered`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al marcar como entregada');
      throw new Error(errorMessage);
    }
  }

  // Marcar grupo de reservas como vendidas
  async markGroupAsSold(accessCodeId) {
    try {
      const response = await this.api.patch(`/group/${accessCodeId}/mark-sold`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al marcar grupo como vendido');
      throw new Error(errorMessage);
    }
  }

  // Marcar grupo de reservas como entregadas
  async markGroupAsDelivered(accessCodeId) {
    try {
      const response = await this.api.patch(`/group/${accessCodeId}/mark-delivered`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al marcar grupo como entregado');
      throw new Error(errorMessage);
    }
  }

  // Devolver una reserva individual
  async returnReservation(id, reason = '', fromAdmin = false) {
    try {
      const response = await this.api.patch(`/${id}/return`, { reason, fromAdmin });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al devolver la reserva');
      throw new Error(errorMessage);
    }
  }

  // Devolver grupo de reservas
  async returnGroupReservations(accessCodeId, reason = '', fromAdmin = false) {
    try {
      const response = await this.api.patch(`/group/${accessCodeId}/return`, { reason, fromAdmin });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al devolver el grupo de reservas');
      throw new Error(errorMessage);
    }
  }

  // Cancelar grupo de reservas
  async cancelGroupReservations(accessCodeId) {
    try {
      const response = await this.api.patch(`/group/${accessCodeId}/cancel`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cancelar el grupo de reservas');
      throw new Error(errorMessage);
    }
  }

  // Eliminar una reserva
  async deleteReservation(id) {
    try {
      await this.api.delete(`/${id}`);
      return true;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al eliminar reserva');
      throw new Error(errorMessage);
    }
  }

  // Crear una nueva reserva
  async createReservation(reservationData) {
    try {
      const response = await this.api.post('/', reservationData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al crear reserva');
      throw new Error(errorMessage);
    }
  }

  // Modificar reservas existentes (devoluciones, cancelaciones y nuevas creaciones) con un solo operationId
  async modifyExistingReservations(modifyData) {
    try {
      const response = await this.api.post('/modify-existing', modifyData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al modificar reservas existentes');
      throw new Error(errorMessage);
    }
  }

  // Exportar reservas agrupadas a Excel
  async exportGroupedReservations(filters = {}) {
    try {
      const requestData = {
        eventId: filters.eventId || undefined,
        studentId: filters.studentId || undefined,
        accessCode: filters.accessCode || undefined,
        status: filters.status && filters.status !== 'all' ? filters.status : undefined,
      };
      
      const response = await this.api.post('/grouped/export', requestData, {
        responseType: 'blob'
      });
      
      // Crear un blob y descargar el archivo
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Reservas-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al exportar reservas');
      throw new Error(errorMessage);
    }
  }

  // Método para obtener mensajes de error
  getErrorMessage(error, defaultMessage) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.message) {
      return error.message;
    }
    return defaultMessage;
  }
}

const reservationService = new ReservationService();
export default reservationService; 